import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BimClashFocusMode,
  BimClashItem,
  BimClashMode,
  BimClashProgress,
  BimClashResult,
  BimViewerHandle
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { ALL_RULE_ID, IClashCustomRule, buildClashRules } from '../../clashRules';

export interface IClashModel {
  urn: string;
  name: string;
}

interface IUseClashPanel {
  open: boolean;
  handleRef: MutableRefObject<BimViewerHandle | null>;
  models: IClashModel[];
}

export type ClashTab = 'run' | 'rules' | 'results';
export type ClashRunState = 'idle' | 'running' | 'done' | 'error' | 'unsupported';

export const DEFAULT_TOLERANCE_MM = 2;
export const DEFAULT_CLEARANCE_MM = 50;

const urnsKey = (urns: string[]): string => [...urns].sort().join('|');

export const useClashPanel = ({ open, handleRef, models }: IUseClashPanel) => {
  const { t } = useTranslation('federatedViewer');
  const [tab, setTab] = useState<ClashTab>('run');

  // Configuração só na sessão por enquanto: salvar por obra é a #12468.
  const [mode, setMode] = useState<BimClashMode>('hard');
  const [toleranceMm, setToleranceMm] = useState(DEFAULT_TOLERANCE_MM);
  const [clearanceMm, setClearanceMm] = useState(DEFAULT_CLEARANCE_MM);
  const [crossModelOnly, setCrossModelOnly] = useState(true);
  // Guarda os DESMARCADOS: modelo que entra na cena já vem marcado.
  const [excludedUrns, setExcludedUrns] = useState<Set<string>>(new Set());
  const selectedUrns = useMemo(
    () => models.map((model) => model.urn).filter((urn) => !excludedUrns.has(urn)),
    [models, excludedUrns]
  );
  const [enabledRules, setEnabledRules] = useState<Set<string>>(new Set([ALL_RULE_ID]));
  const [custom, setCustom] = useState<IClashCustomRule>({ typesA: [], typesB: [] });

  const [runState, setRunState] = useState<ClashRunState>('idle');
  const [progress, setProgress] = useState<BimClashProgress | null>(null);
  const [result, setResult] = useState<BimClashResult | null>(null);
  const [resultUrns, setResultUrns] = useState('');
  const [activeClashId, setActiveClashId] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState<BimClashFocusMode>('ghost');
  const [highlightAll, setHighlightAll] = useState(false);

  const nameOf = useCallback((id: string) => t(`clash.rules.${id}`), [t]);
  const rules = useMemo(
    () => buildClashRules(enabledRules, custom, nameOf),
    [enabledRules, custom, nameOf]
  );

  // Tipos com geometria na cena, para a regra personalizada. Relidos ao abrir
  // a aba de regras: modelos entram e saem da cena com o painel aberto.
  const [types, setTypes] = useState<{ ifcType: string; count: number }[]>([]);
  useEffect(() => {
    if (open && tab === 'rules') { setTypes(handleRef.current?.getClashTypes(selectedUrns) ?? []); }
  }, [open, tab, selectedUrns, handleRef]);

  const toggleModel = useCallback((urn: string, selected: boolean) => {
    setExcludedUrns((current) => {
      const next = new Set(current);
      if (selected) { next.delete(urn); } else { next.add(urn); }
      return next;
    });
  }, []);

  const toggleRule = useCallback((id: string, enabled: boolean) => {
    setEnabledRules((current) => {
      const next = new Set(current);
      if (enabled) { next.add(id); } else { next.delete(id); }
      return next;
    });
  }, []);

  const run = useCallback(async () => {
    const handle = handleRef.current;
    if (!handle || rules.length === 0 || selectedUrns.length === 0) { return; }
    setRunState('running');
    setProgress(null);
    setActiveClashId(null);
    try {
      const next = await handle.runClash(
        rules,
        {
          mode,
          models: selectedUrns,
          crossModelOnly: crossModelOnly && selectedUrns.length > 1,
          ...(mode === 'hard' ? { tolerance: toleranceMm / 1000 } : { clearance: clearanceMm / 1000 })
        },
        setProgress
      );
      if (!next) { setRunState('unsupported'); return; }
      setResult(next);
      setResultUrns(urnsKey(selectedUrns));
      setRunState('done');
      setTab('results');
    } catch (err) {
      // Cancelado (botão, modelo removido ou nova execução): volta ao que havia.
      if (err instanceof DOMException && err.name === 'AbortError') {
        setRunState((current) => (current === 'running' ? (result ? 'done' : 'idle') : current));
        return;
      }
      setRunState('error');
    } finally {
      setProgress(null);
    }
  }, [handleRef, rules, mode, crossModelOnly, toleranceMm, clearanceMm, selectedUrns, result]);

  const cancel = useCallback(() => handleRef.current?.cancelClash(), [handleRef]);

  // Sair da página não deixa o motor trabalhando à toa.
  useEffect(() => () => handleRef.current?.cancelClash(), [handleRef]);

  // A pintura segue o resultado: verificação nova repinta, se estiver ligada.
  useEffect(() => {
    handleRef.current?.setClashHighlight(highlightAll);
  }, [handleRef, highlightAll, result]);

  const clear = useCallback(() => {
    handleRef.current?.clearClash();
    setResult(null);
    setRunState('idle');
    setActiveClashId(null);
    setHighlightAll(false);
  }, [handleRef]);

  const focus = useCallback((clash: BimClashItem) => {
    setActiveClashId(clash.id);
    handleRef.current?.focusClash(clash.a, clash.b, focusMode);
  }, [handleRef, focusMode]);

  // Contagem por issue, na ordem em que os conflitos vêm (já ordenados por issue).
  const issueCounts = useMemo(() => {
    const counts: number[] = [];
    let current = -1;
    for (const clash of result?.clashes ?? []) {
      if (clash.issue !== current) { counts.push(0); current = clash.issue; }
      counts[counts.length - 1] += 1;
    }
    return counts;
  }, [result]);

  return {
    tab,
    setTab,
    models,
    selectedUrns,
    toggleModel,
    mode,
    setMode,
    toleranceMm,
    setToleranceMm,
    clearanceMm,
    setClearanceMm,
    crossModelOnly,
    setCrossModelOnly,
    enabledRules,
    toggleRule,
    custom,
    setCustom,
    types,
    activeRuleCount: rules.length,
    canRun: rules.length > 0 && selectedUrns.length > 0 && runState !== 'running',
    runState,
    progress,
    result,
    stale: !!result && resultUrns !== urnsKey(selectedUrns),
    issueCounts,
    nameOf,
    activeClashId,
    focusMode,
    setFocusMode,
    highlightAll,
    setHighlightAll,
    run,
    cancel,
    clear,
    focus
  };
};
